/**
 * Kombinierte Profil-Matrix: Denkmodus × Lerntyp × Tool-Präferenz
 * Liefert angepasste Strategien und Maßnahmen.
 */
export const profileMatrix = {
  // Navigator-Kombinationen
  structure_visual: {
    headline: "Der strukturierte Visualisierer",
    strategy: "Nutze annotierte Video-Playlists als Lernplan-Gerüst.",
    tools: ["YouTube-Playlists (strukturiert)", "GeoGebra mit Schritt-Protokoll", "Animierte Herleitungen"],
    measures: [
      "Erstelle eine Video-Checkliste pro Themenblock.",
      "Dokumentiere jeden GeoGebra-Versuch in einem Lernprotokoll.",
      "Mappe Animationen auf dein Formelblatt.",
    ],
  },
  structure_interactive: {
    headline: "Der planende Entdecker",
    strategy: "Kombiniere strukturierte Aufgabenpläne mit interaktiver Erkundung.",
    tools: ["GeoGebra (parametrisch)", "KI-Chat als Tutor", "Workflow-Boards"],
    measures: [
      "Definiere vor jeder GeoGebra-Session ein klares Lernziel.",
      "Nutze KI-Chats für gezielte Nachfragen nach dem Schema: Problem → Hypothese → Check.",
      "Führe ein digitales Board mit abgehakten Experimenten.",
    ],
  },
  structure_systematic: {
    headline: "Der System-Architekt",
    strategy: "Maximale Kontrolle durch strukturierte Checklisten und Boards.",
    tools: ["Kanban-Boards", "Checklisten", "Leitner-System (thematisch)"],
    measures: [
      "Erstelle für jedes Thema eine 5-Punkte-Checkliste.",
      "Pflege ein Kanban-Board: Offen → In Bearbeitung → Geprüft.",
      "Lege wöchentliche Board-Reviews fest.",
    ],
  },
  structure_repetitive: {
    headline: "Der strukturierte Wiederholer",
    strategy: "Plane Wiederholungszyklen systematisch und teste dich regelmäßig.",
    tools: ["Leitner-System", "Quizzes (terminiert)", "Checklisten"],
    measures: [
      "Plane feste Quiz-Slots im Wochenplan.",
      "Sortiere Karteikarten nach Themenblöcken.",
      "Nutze Quizzes als wöchentlichen Fortschrittscheck.",
    ],
  },

  // Sprinter-Kombinationen
  speed_visual: {
    headline: "Der schnelle Visualisierer",
    strategy: "Kurze, prägnante Video-Clips als Speed-Check vor der Aufgabe.",
    tools: ["YouTube Shorts / Clips", "Animationen (< 3 min)", "GeoGebra Quick-Checks"],
    measures: [
      "Schaue vor jeder Aufgabe einen 2-Minuten-Clip zur Methode.",
      "Nutze GeoGebra für schnelle Plausibilitätschecks.",
      "Erstelle eigene Skizzen-Screenshots als Gedächtnisstütze.",
    ],
  },
  speed_interactive: {
    headline: "Der experimentelle Sprinter",
    strategy: "Schnelles Ausprobieren mit sofortigem KI-Feedback.",
    tools: ["KI-Chat (Sofort-Feedback)", "Simulationen", "GeoGebra"],
    measures: [
      "Nutze KI-Chat als Echtzeit-Korrektor für deine Rechenschritte.",
      "Setze Simulationen gezielt für Plausibilitätsproben ein.",
      "Begrenze jede Experimentiersession auf 10 Minuten.",
    ],
  },
  speed_systematic: {
    headline: "Der disziplinierte Sprinter",
    strategy: "Checklisten als Bremse gegen Flüchtigkeitsfehler.",
    tools: ["Fehler-Checklisten", "Boards (Tages-Ziele)", "Leitner-System"],
    measures: [
      "Hake jeden Rechenschritt auf einer Mini-Checkliste ab.",
      "Setze dir täglich 3 Pflicht-Aufgaben auf deinem Board.",
      "Wiederhole Fehler-Typen gezielt im Leitner-System.",
    ],
  },
  speed_repetitive: {
    headline: "Der Speed-Tester",
    strategy: "Tägliche Kurzquizzes als Trainingseinheit für Präzision.",
    tools: ["Quizzes (täglich, 5 min)", "Leitner-System", "KI-Chats"],
    measures: [
      "Starte jeden Lerntag mit einem 5-Fragen-Quiz.",
      "Lege Fehler-Karten ins Leitner-System zur Wiederholung.",
      "Nutze KI-Chats für sofortige Erklärungen bei Quiz-Fehlern.",
    ],
  },

  // Explorer-Kombinationen
  intuition_visual: {
    headline: "Der visuelle Explorer",
    strategy: "Animationen und Graphen als primäre Erkenntnisquelle.",
    tools: ["3Blue1Brown / Animationen", "GeoGebra (grafisch)", "YouTube"],
    measures: [
      "Starte jedes Thema mit einer GeoGebra-Erkundung.",
      "Skizziere nach jedem Video dein mentales Modell.",
      "Baue eine persönliche Bild-Bibliothek zentraler Konzepte.",
    ],
  },
  intuition_interactive: {
    headline: "Der intuitive Entdecker",
    strategy: "Interaktive Simulationen als Sprungbrett zur Formel.",
    tools: ["Simulationen", "GeoGebra", "KI-Chat"],
    measures: [
      "Verändere Parameter in Simulationen und notiere deine Beobachtungen.",
      "Formuliere deine Intuition in Worte – lasse die KI sie formalisieren.",
      "Baue die Brücke: Bild → Beobachtung → Term.",
    ],
  },
  intuition_systematic: {
    headline: "Der strukturierte Explorer",
    strategy: "Checklisten übersetzen deine Bilder in systematische Schritte.",
    tools: ["Visualisierungs-Checklisten", "Boards", "Animationen"],
    measures: [
      "Erstelle für jedes Thema eine 'Bild-zu-Formel'-Checkliste.",
      "Nutze ein Board, um deine Skizzen und Formeln zu verbinden.",
      "Plane wöchentliche Visualisierungs-Sessions.",
    ],
  },
  intuition_repetitive: {
    headline: "Der intuitive Wiederholer",
    strategy: "Bild-Karteikarten und visuelle Quizzes festigen das Gespür.",
    tools: ["Bild-Karteikarten", "Quizzes mit Graphen", "Animationen"],
    measures: [
      "Erstelle Leitner-Karten mit Skizze (Vorderseite) und Formel (Rückseite).",
      "Wähle Quizzes, die Graphen zum Ablesen nutzen.",
      "Wiederhole wöchentlich deine Bild-Bibliothek.",
    ],
  },

  // Architekt-Kombinationen
  model_visual: {
    headline: "Der theoretische Visualisierer",
    strategy: "Animierte Beweise und Herleitungen als Vertiefungsmedium.",
    tools: ["3Blue1Brown", "Animierte Beweise", "GeoGebra (Beweise)"],
    measures: [
      "Suche gezielt nach animierten Beweisvideos für jedes neue Theorem.",
      "Rekonstruiere Animationen in GeoGebra eigenständig.",
      "Erstelle ein visuelles Glossar aller zentralen Definitionen.",
    ],
  },
  model_interactive: {
    headline: "Der interaktive Theoretiker",
    strategy: "KI-Dialoge und Simulationen zur Tiefenverifikation.",
    tools: ["KI-Chat (Sokrates-Methode)", "Simulationen", "GeoGebra"],
    measures: [
      "Führe Sokrates-Dialoge mit der KI: Stelle Definitionen in Frage.",
      "Simuliere Grenzfälle in GeoGebra zur Überprüfung deiner Modelle.",
      "Dokumentiere KI-Gespräche als Lernprotokoll.",
    ],
  },
  model_systematic: {
    headline: "Der vollständige System-Denker",
    strategy: "Boards und Checklisten für lückenloses Theorieverständnis.",
    tools: ["Wissens-Boards", "Definitions-Checklisten", "Leitner-System"],
    measures: [
      "Erstelle ein Board mit allen Definitionen, Sätzen und Beweisen.",
      "Checkliste: Habe ich Def. → Satz → Beweis → Beispiel vollständig?",
      "Leitner-System für formale Definitionen und Sätze.",
    ],
  },
  model_repetitive: {
    headline: "Der systematische Theoretiker",
    strategy: "Quizzes und Karteikarten für formale Präzision.",
    tools: ["Definitions-Quizzes", "Leitner-System (Sätze)", "KI-Chats"],
    measures: [
      "Erstelle Leitner-Karten: Satz-Name (vorne) → Aussage + Beweis-Idee (hinten).",
      "Teste täglich 5 Definitionen per Quiz.",
      "Nutze KI-Chats zur Überprüfung deiner Formulierungen.",
    ],
  },

  // Guardian-Kombinationen
  analysis_visual: {
    headline: "Der vorsichtige Visualisierer",
    strategy: "Kurze Erklärvideos bauen Schritt für Schritt Vertrauen auf.",
    tools: ["YouTube (Schritt-für-Schritt)", "Animationen", "GeoGebra"],
    measures: [
      "Schaue kurze Clips und starte danach sofort mit einer Aufgabe.",
      "Nutze GeoGebra als 'Sicherheitsnetz' zur Ergebnisvisualisierung.",
      "Führe ein Erfolgsjournal mit Screenshots deiner GeoGebra-Ergebnisse.",
    ],
  },
  analysis_interactive: {
    headline: "Der vorsichtige Entdecker",
    strategy: "KI-Chats als sicherer Gesprächspartner ohne Bewertungsdruck.",
    tools: ["KI-Chat (ohne Druck)", "Simulationen", "GeoGebra"],
    measures: [
      "Nutze KI-Chats als Übungspartner, der nie ungeduldig wird.",
      "Experimentiere in Simulationen ohne Angst vor 'falsch'.",
      "Starte jeden Tag mit einem kleinen GeoGebra-Erfolgserlebnis.",
    ],
  },
  analysis_systematic: {
    headline: "Der sichere Systematiker",
    strategy: "Checklisten geben Sicherheit und reduzieren Entscheidungsangst.",
    tools: ["Schritt-Checklisten", "Boards", "Leitner-System"],
    measures: [
      "Erstelle für jeden Aufgabentyp eine Schritt-für-Schritt-Checkliste.",
      "Nutze ein Board, um abgeschlossene Aufgaben sichtbar zu machen.",
      "Fange mit dem leichtesten Leitner-Fach an.",
    ],
  },
  analysis_repetitive: {
    headline: "Der behutsame Wiederholer",
    strategy: "Sanfte Quizzes mit bekannten Inhalten zum Vertrauensaufbau.",
    tools: ["Quizzes (bekannte Themen)", "Leitner-System", "KI-Chats"],
    measures: [
      "Starte Quizzes nur mit Themen, die du bereits kennst.",
      "Schalte neue Karten im Leitner-System erst frei, wenn du sicher bist.",
      "Nutze KI-Chats für Ermutigung und Erklärung nach Fehlern.",
    ],
  },
};

export function getProfile(modeId, toolId) {
  const key = `${modeId}_${toolId}`;
  return profileMatrix[key] || null;
}